import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AlertConfig, AlertType, useAlert } from "./useAlert";

export type QueuedAlert = AlertConfig;

export type QueuedAlertsNavigationState = {
    message?: string;
    type?: AlertType;
    duration?: number;
    autoHide?: boolean;
    alerts?: QueuedAlert[];
};

interface UseQueuedAlertsConfig {
    defaultDuration?: number;
    defaultType?: AlertType;
    autoHide?: boolean;
}

interface UseQueuedAlertsReturn {
    alert: ReturnType<typeof useAlert>["alert"];
    isVisible: boolean;
    showAlert: (config: AlertConfig) => void;
    hideAlert: () => void;
    handleCloseAlert: () => void;
    showQueuedAlerts: (alerts: QueuedAlert[]) => void;
}

export const useQueuedAlerts = (config: UseQueuedAlertsConfig = {}): UseQueuedAlertsReturn => {
    const {
        defaultDuration = 3000,
        defaultType = "success",
        autoHide = true
    } = config;

    const location = useLocation();
    const { showAlert: baseShowAlert, hideAlert: baseHideAlert, alert, isVisible } = useAlert({
        fromLocation: false,
        defaultDuration,
        defaultType,
        autoHide
    });

    const queueRef = useRef<QueuedAlert[]>([]);
    const queueTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const queueActiveRef = useRef(false);

    const clearQueueTimer = useCallback(() => {
        if (queueTimeoutRef.current) {
            clearTimeout(queueTimeoutRef.current);
            queueTimeoutRef.current = null;
        }
    }, []);

    const showNextQueuedAlert = useCallback(() => {
        const nextAlert = queueRef.current.shift();
        if (!nextAlert) {
            queueActiveRef.current = false;
            return;
        }

        clearQueueTimer();
        baseShowAlert({
            message: nextAlert.message,
            type: nextAlert.type ?? defaultType,
            duration: nextAlert.duration ?? defaultDuration,
            // Queue timing is controlled by this hook.
            autoHide: false
        });

        const shouldAutoHide = nextAlert.autoHide ?? autoHide;
        const duration = nextAlert.duration ?? defaultDuration;
        if (shouldAutoHide && duration > 0) {
            queueTimeoutRef.current = setTimeout(() => {
                baseHideAlert();
                showNextQueuedAlert();
            }, duration);
        }
    }, [autoHide, baseHideAlert, baseShowAlert, clearQueueTimer, defaultDuration, defaultType]);

    const showQueuedAlerts = useCallback((alertsToShow: QueuedAlert[]) => {
        if (!alertsToShow.length) return;
        clearQueueTimer();
        queueRef.current = [...alertsToShow];
        queueActiveRef.current = queueRef.current.length > 1;
        showNextQueuedAlert();
    }, [clearQueueTimer, showNextQueuedAlert]);

    const showAlert = useCallback((alertConfig: AlertConfig) => {
        queueRef.current = [];
        queueActiveRef.current = false;
        clearQueueTimer();
        baseShowAlert(alertConfig);
    }, [baseShowAlert, clearQueueTimer]);

    const hideAlert = useCallback(() => {
        queueRef.current = [];
        queueActiveRef.current = false;
        clearQueueTimer();
        baseHideAlert();
    }, [baseHideAlert, clearQueueTimer]);

    const handleCloseAlert = useCallback(() => {
        baseHideAlert();
        clearQueueTimer();
        if (queueActiveRef.current) {
            showNextQueuedAlert();
        }
    }, [baseHideAlert, clearQueueTimer, showNextQueuedAlert]);

    useEffect(() => {
        const navState = (location.state ?? {}) as QueuedAlertsNavigationState;
        const alertsFromState = navState.alerts?.length
            ? navState.alerts
            : navState.message
                ? [{
                    message: navState.message,
                    type: navState.type,
                    duration: navState.duration,
                    autoHide: navState.autoHide
                }]
                : [];

        if (!alertsFromState.length) return;
        showQueuedAlerts(alertsFromState);
    }, [location.key, location.state, showQueuedAlerts]);

    useEffect(() => () => clearQueueTimer(), [clearQueueTimer]);

    return {
        alert,
        isVisible,
        showAlert,
        hideAlert,
        handleCloseAlert,
        showQueuedAlerts
    };
};

